import React, { useState, useEffect } from 'react';
import API_BASE from '../config';

const countryCodes = [
  { code: '+39', country: 'Italy' },
  { code: '+1', country: 'USA/Canada' },
  { code: '+33', country: 'France' },
  { code: '+49', country: 'Germany' },
  { code: '+44', country: 'UK' },
  { code: '+34', country: 'Spain' },
  { code: '+41', country: 'Switzerland' },
  { code: '+43', country: 'Austria' },
  { code: '+32', country: 'Belgium' },
  { code: '+31', country: 'Netherlands' }
];

function Dashboard({ accessCode, adminUser }) {
  const [students, setStudents] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [config, setConfig] = useState({ menus: [], relations: [] });
  const [loading, setLoading] = useState(true);
  const [collapsedClasses, setCollapsedClasses] = useState({});

  // View/Edit panel state
  const [viewingReg, setViewingReg] = useState(null);
  const [editingReg, setEditingReg] = useState(null);
  const [editData, setEditData] = useState(null);
  const [panelError, setPanelError] = useState('');
  const [panelSuccess, setPanelSuccess] = useState('');

  // Create panel state
  const [creatingForStudent, setCreatingForStudent] = useState(null);
  const [createData, setCreateData] = useState(null);

  // PDF dropdown state
  const [pdfDropdownId, setPdfDropdownId] = useState(null);

  // Student name edit state
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [editStudentFirst, setEditStudentFirst] = useState('');
  const [editStudentLast, setEditStudentLast] = useState('');

  const togglePdfDropdown = (regId) => {
    setPdfDropdownId(prev => prev === regId ? null : regId);
  };

  // Close PDF dropdown when clicking anywhere
  useEffect(() => {
    const handleClickOutside = () => setPdfDropdownId(null);
    if (pdfDropdownId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [pdfDropdownId]);
  const [sortField, setSortField] = useState('student');
  const [sortDir, setSortDir] = useState('asc');
  const [filterRegistered, setFilterRegistered] = useState('all'); // all, yes, no
  const [filterPaid, setFilterPaid] = useState('all'); // all, yes, no
  const [filterLiberatoria, setFilterLiberatoria] = useState('all'); // all, yes, no
  const [filterStudent, setFilterStudent] = useState('');

  const isOrganizer = adminUser.role === 'organizer';
  const isRepLocked = !isOrganizer && config.representativeLockDate && new Date() > new Date(config.representativeLockDate);
  const ALL_CLASSES = config.classes || ['1A','1B','1C','2A','2B','2C','3A','3B','3C'];
  const classes = isOrganizer ? ALL_CLASSES : [adminUser.class];

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [studentsRes, registrationsRes, configRes] = await Promise.all([
        fetch(`${API_BASE}/students`),
        fetch(`${API_BASE}/registrations`, { headers: { 'x-access-code': accessCode } }),
        fetch(`${API_BASE}/config`)
      ]);
      setStudents(await studentsRes.json());
      setRegistrations(await registrationsRes.json());
      setConfig(await configRes.json());
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    }
    setLoading(false);
  };

  const getMenuPrice = (menuName) => {
    const menu = config.menus.find(m => m.name === menuName);
    return menu ? menu.price : 0;
  };

  const getRegistrationCost = (reg) => {
    return reg.participants.reduce((sum, p) => sum + getMenuPrice(p.menu), 0);
  };

  const togglePayment = async (regId, currentPaid) => {
    try {
      const response = await fetch(`${API_BASE}/admin/registrations/${regId}/payment`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode },
        body: JSON.stringify({ paid: !currentPaid })
      });
      if (response.ok) {
        setRegistrations(prev => prev.map(r => r.id === regId ? { ...r, paid: !currentPaid } : r));
        if (viewingReg && viewingReg.id === regId) setViewingReg(prev => ({ ...prev, paid: !currentPaid }));
      }
    } catch (err) { console.error('Failed to toggle payment:', err); }
  };

  const toggleCheckin = async (regId, currentCheckedIn) => {
    try {
      const response = await fetch(`${API_BASE}/admin/registrations/${regId}/checkin`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode },
        body: JSON.stringify({ checkedIn: !currentCheckedIn })
      });
      if (response.ok) {
        setRegistrations(prev => prev.map(r => r.id === regId ? { ...r, checkedIn: !currentCheckedIn } : r));
        if (viewingReg && viewingReg.id === regId) setViewingReg(prev => ({ ...prev, checkedIn: !currentCheckedIn }));
      }
    } catch (err) { console.error('Failed to toggle check-in:', err); }
  };

  const toggleLiberatoria = async (regId, currentValue) => {
    try {
      const response = await fetch(`${API_BASE}/admin/registrations/${regId}/liberatoria`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode },
        body: JSON.stringify({ liberatoriaReceived: !currentValue })
      });
      if (response.ok) {
        setRegistrations(prev => prev.map(r => r.id === regId ? { ...r, liberatoriaReceived: !currentValue } : r));
        if (viewingReg && viewingReg.id === regId) setViewingReg(prev => ({ ...prev, liberatoriaReceived: !currentValue }));
      }
    } catch (err) { console.error('Failed to toggle liberatoria:', err); }
  };

  const saveStudentName = async (studentId) => {
    if (!editStudentFirst.trim() || !editStudentLast.trim()) return;
    try {
      const response = await fetch(`${API_BASE}/students/${studentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode },
        body: JSON.stringify({ firstName: editStudentFirst.trim(), lastName: editStudentLast.trim() })
      });
      if (response.ok) {
        setEditingStudentId(null);
        fetchData();
      }
    } catch (err) { console.error('Failed to update student:', err); }
  };

  const toggleCollapse = (cls) => { setCollapsedClasses(prev => ({ ...prev, [cls]: !prev[cls] })); };
  const collapseAll = () => { const all = {}; classes.forEach(c => { all[c] = true; }); setCollapsedClasses(all); };
  const expandAll = () => { setCollapsedClasses({}); };

  const getClassData = (cls) => {
    const classStudents = students.filter(s => s.class === cls);
    const classRegistrations = registrations.filter(r => r.student.class === cls);
    const studentRows = classStudents.map(s => {
      const reg = classRegistrations.find(
        r => r.student.firstName.trim().replace(/\s+/g, ' ').toLowerCase() === s.firstName.trim().replace(/\s+/g, ' ').toLowerCase() &&
             r.student.lastName.trim().replace(/\s+/g, ' ').toLowerCase() === s.lastName.trim().replace(/\s+/g, ' ').toLowerCase()
      );
      const siblingInfo = findSiblingRegistration(s);
      return { student: s, registration: reg, siblingInfo };
    });
    const totalRegistrations = classRegistrations.length;
    // Count sibling students as registered too
    const siblingCount = studentRows.filter(r => !r.registration && r.siblingInfo).length;
    const totalRegistered = totalRegistrations + siblingCount;
    const totalCost = classRegistrations.reduce((sum, r) => sum + getRegistrationCost(r), 0);
    const totalPaid = classRegistrations.filter(r => r.paid).reduce((sum, r) => sum + getRegistrationCost(r), 0);
    const totalCheckedIn = classRegistrations.filter(r => r.checkedIn || r.checkedInPartial).length;
    return { studentRows, totalRegistrations, totalRegistered, totalCost, totalPaid, totalMissing: totalCost - totalPaid, totalCheckedIn };
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIcon = (field) => {
    if (sortField !== field) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  const applyFiltersAndSort = (rows) => {
    let filtered = rows;

    if (filterStudent.trim()) {
      const search = filterStudent.trim().toLowerCase();
      filtered = filtered.filter(r => 
        `${r.student.firstName} ${r.student.lastName}`.toLowerCase().includes(search)
      );
    }

    if (filterRegistered === 'yes') {
      filtered = filtered.filter(r => r.registration || r.siblingInfo);
    } else if (filterRegistered === 'no') {
      filtered = filtered.filter(r => !r.registration && !r.siblingInfo);
    }

    if (filterPaid === 'yes') {
      filtered = filtered.filter(r => r.registration && r.registration.paid);
    } else if (filterPaid === 'no') {
      filtered = filtered.filter(r => r.registration && !r.registration.paid);
    }

    if (filterLiberatoria === 'yes') {
      filtered = filtered.filter(r => r.registration && (r.registration.liberatoriaReceived || (r.registration.liberatoria && r.registration.liberatoria.signedElectronically)));
    } else if (filterLiberatoria === 'no') {
      filtered = filtered.filter(r => r.registration && !r.registration.liberatoriaReceived && !(r.registration.liberatoria && r.registration.liberatoria.signedElectronically));
    }

    filtered.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'student') {
        cmp = `${a.student.lastName} ${a.student.firstName}`.localeCompare(`${b.student.lastName} ${b.student.firstName}`);
      } else if (sortField === 'registered') {
        const aVal = a.registration ? 2 : (a.siblingInfo ? 1 : 0);
        const bVal = b.registration ? 2 : (b.siblingInfo ? 1 : 0);
        cmp = aVal - bVal;
      } else if (sortField === 'paid') {
        const aVal = a.registration ? (a.registration.paid ? 1 : 0) : -1;
        const bVal = b.registration ? (b.registration.paid ? 1 : 0) : -1;
        cmp = aVal - bVal;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return filtered;
  };

  // Find if a student appears as a sibling-student participant on another registration
  const findSiblingRegistration = (s) => {
    for (const reg of registrations) {
      for (const p of reg.participants) {
        if (p.siblingStudent && p.siblingClass === s.class) {
          if (p.firstName.trim().toLowerCase() === s.firstName.toLowerCase() &&
              p.lastName.trim().toLowerCase() === s.lastName.toLowerCase()) {
            return { registration: reg, participant: p };
          }
        }
      }
    }
    return null;
  };

  // View panel
  const openView = (reg) => { setViewingReg(reg); setEditingReg(null); setEditData(null); setPanelError(''); setPanelSuccess(''); };
  const closePanel = () => { setViewingReg(null); setEditingReg(null); setEditData(null); setCreatingForStudent(null); setCreateData(null); setPanelError(''); setPanelSuccess(''); };

  // Edit panel
  const openEdit = (reg) => {
    const phoneParts = reg.parentContact.phone.split('-');
    setEditData({
      id: reg.id,
      parentContact: { email: reg.parentContact.email, countryCode: phoneParts[0] || '+39', phone: phoneParts[1] || '' },
      participants: JSON.parse(JSON.stringify(reg.participants)),
      delegationActive: reg.delegation?.active || false,
      delegation: reg.delegation?.active ? {
        delegatedStudent: { ...reg.delegation.delegatedStudent },
        delegatedParent: { ...reg.delegation.delegatedParent }
      } : {
        delegatedStudent: { firstName: '', lastName: '', class: '' },
        delegatedParent: { firstName: '', lastName: '' }
      }
    });
    setEditingReg(reg);
    setViewingReg(null);
    setPanelError('');
    setPanelSuccess('');
  };

  const openEditFromView = () => { if (viewingReg) openEdit(viewingReg); };

  const updateParticipant = (index, field, value) => {
    const updated = { ...editData };
    updated.participants[index][field] = value;
    setEditData(updated);
  };

  const addParticipant = () => {
    const updated = { ...editData };
    updated.participants.push({ firstName: '', lastName: '', relation: config.relations[1] || 'Genitore', menu: config.menus[0]?.name || 'Hamburger' });
    setEditData(updated);
  };

  const removeParticipant = (index) => {
    if (index === 0) return;
    const updated = { ...editData };
    updated.participants = updated.participants.filter((_, i) => i !== index);
    setEditData(updated);
  };

  const toggleEditDelegation = (active) => {
    const updated = { ...editData, delegationActive: active };
    if (active) {
      // Keep only the student (first participant)
      updated.participants = [updated.participants[0]];
    }
    setEditData(updated);
  };

  const saveEdit = async () => {
    setPanelError('');
    setPanelSuccess('');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editData.parentContact.email)) { setPanelError('Inserire un indirizzo email valido.'); return; }
    const phoneRegex = /^\d{6,15}$/;
    if (!phoneRegex.test(editData.parentContact.phone)) { setPanelError('Inserire un numero di telefono valido.'); return; }
    if (editData.participants.some(p => !p.firstName.trim() || !p.lastName.trim())) { setPanelError('Compilare nome e cognome di tutti i partecipanti.'); return; }

    // Validate sibling students
    for (const p of editData.participants) {
      if (p.siblingStudent && p.relation === 'Fratello/Sorella') {
        if (!p.siblingClass) { setPanelError(`Selezionare una classe per il fratello/sorella ${p.firstName} ${p.lastName}.`); return; }
        try {
          const sibRes = await fetch(`${API_BASE}/validate-student-exists`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ firstName: p.firstName.trim(), lastName: p.lastName.trim(), siblingClass: p.siblingClass })
          });
          const sibData = await sibRes.json();
          if (!sibData.valid) {
            if (sibData.suggestion) {
              setPanelError(`${sibData.error} Suggerimento: ${sibData.suggestion.firstName} ${sibData.suggestion.lastName} (Classe ${sibData.suggestion.class}).`);
            } else {
              setPanelError(sibData.error);
            }
            return;
          }
        } catch (err) { setPanelError('Errore nella verifica del fratello/sorella.'); return; }
      }
    }

    // Validate delegation if active
    if (editData.delegationActive) {
      const ds = editData.delegation.delegatedStudent;
      const dp = editData.delegation.delegatedParent;
      if (!ds.firstName.trim() || !ds.lastName.trim() || !ds.class) { setPanelError('Compilare tutti i campi dello studente delegato.'); return; }
      if (!dp.firstName.trim() || !dp.lastName.trim()) { setPanelError('Compilare il nome del genitore delegato.'); return; }
      // Validate delegated student with fuzzy matching
      try {
        const sibRes = await fetch(`${API_BASE}/validate-student-exists`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ firstName: ds.firstName.trim(), lastName: ds.lastName.trim(), siblingClass: ds.class })
        });
        const sibData = await sibRes.json();
        if (!sibData.valid) {
          if (sibData.suggestion) {
            setPanelError(`${sibData.error} Suggerimento: ${sibData.suggestion.firstName} ${sibData.suggestion.lastName} (Classe ${sibData.suggestion.class}).`);
          } else {
            setPanelError(sibData.error || 'Studente delegato non trovato.');
          }
          return;
        }
      } catch (err) { setPanelError('Errore nella verifica dello studente delegato.'); return; }
    } else {
      const hasParent = editData.participants.some(p => p.relation === 'Genitore' || p.relation === 'Altro Genitore');
      if (!hasParent) { setPanelError('Almeno un genitore deve accompagnare il bambino, oppure abilitare la delega.'); return; }
    }

    try {
      const fullPhone = `${editData.parentContact.countryCode}-${editData.parentContact.phone}`;
      const delegation = editData.delegationActive
        ? { active: true, delegatedStudent: editData.delegation.delegatedStudent, delegatedParent: editData.delegation.delegatedParent }
        : { active: false };
      const response = await fetch(`${API_BASE}/admin/registrations/${editData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode },
        body: JSON.stringify({ parentContact: { ...editData.parentContact, phone: fullPhone }, participants: editData.participants, delegation })
      });
      if (response.ok) {
        setPanelSuccess('Registrazione aggiornata con successo.');
        fetchData();
        setTimeout(() => closePanel(), 1500);
      } else {
        const data = await response.json();
        setPanelError(data.error || 'Errore nel/nella aggiornamento.');
      }
    } catch (err) { setPanelError('Errore nel/nella aggiornamento della registrazione.'); }
  };

  // Create registration
  const openCreate = (s) => {
    setCreatingForStudent(s);
    setCreateData({
      parentContact: { email: '', countryCode: '+39', phone: '' },
      participants: [{ firstName: s.firstName, lastName: s.lastName, relation: 'Studente', menu: config.menus[0]?.name || 'Hamburger', siblingStudent: false, siblingClass: '' }],
      delegationActive: false,
      delegation: { delegatedStudent: { firstName: '', lastName: '', class: '' }, delegatedParent: { firstName: '', lastName: '' } }
    });
    setViewingReg(null);
    setEditingReg(null);
    setEditData(null);
    setPanelError('');
    setPanelSuccess('');
  };

  const saveCreate = async () => {
    setPanelError('');
    setPanelSuccess('');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(createData.parentContact.email)) { setPanelError('Inserire un indirizzo email valido.'); return; }
    const phoneRegex = /^\d{6,15}$/;
    if (!phoneRegex.test(createData.parentContact.phone)) { setPanelError('Inserire un numero di telefono valido.'); return; }
    if (createData.participants.some(p => !p.firstName.trim() || !p.lastName.trim())) { setPanelError('Compilare nome e cognome di tutti i partecipanti.'); return; }

    // Validate sibling students
    for (const p of createData.participants) {
      if (p.siblingStudent && p.relation === 'Fratello/Sorella') {
        if (!p.siblingClass) { setPanelError(`Selezionare una classe per il fratello/sorella ${p.firstName} ${p.lastName}.`); return; }
        try {
          const sibRes = await fetch(`${API_BASE}/validate-student-exists`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ firstName: p.firstName.trim(), lastName: p.lastName.trim(), siblingClass: p.siblingClass })
          });
          const sibData = await sibRes.json();
          if (!sibData.valid) {
            if (sibData.suggestion) {
              setPanelError(`${sibData.error} Suggerimento: ${sibData.suggestion.firstName} ${sibData.suggestion.lastName} (Classe ${sibData.suggestion.class}).`);
            } else {
              setPanelError(sibData.error);
            }
            return;
          }
        } catch (err) { setPanelError('Errore nella verifica del fratello/sorella.'); return; }
      }
    }

    if (!createData.delegationActive) {
      const hasParent = createData.participants.some(p => p.relation === 'Genitore' || p.relation === 'Altro Genitore');
      if (!hasParent) { setPanelError('Almeno un genitore richiesto, oppure abilitare la delega.'); return; }
    } else {
      const ds = createData.delegation.delegatedStudent;
      const dp = createData.delegation.delegatedParent;
      if (!ds.firstName.trim() || !ds.lastName.trim() || !ds.class) { setPanelError('Compilare tutti i campi dello studente delegato.'); return; }
      if (!dp.firstName.trim() || !dp.lastName.trim()) { setPanelError('Compilare il nome del genitore delegato.'); return; }
      // Validate delegated student with fuzzy matching
      try {
        const sibRes = await fetch(`${API_BASE}/validate-student-exists`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ firstName: ds.firstName.trim(), lastName: ds.lastName.trim(), siblingClass: ds.class })
        });
        const sibData = await sibRes.json();
        if (!sibData.valid) {
          if (sibData.suggestion) {
            setPanelError(`${sibData.error} Suggerimento: ${sibData.suggestion.firstName} ${sibData.suggestion.lastName} (Classe ${sibData.suggestion.class}).`);
          } else {
            setPanelError(sibData.error || 'Studente delegato non trovato.');
          }
          return;
        }
      } catch (err) { setPanelError('Errore nella verifica dello studente delegato.'); return; }
    }

    try {
      const fullPhone = `${createData.parentContact.countryCode}-${createData.parentContact.phone}`;
      const delegation = createData.delegationActive
        ? { active: true, delegatedStudent: createData.delegation.delegatedStudent, delegatedParent: createData.delegation.delegatedParent }
        : { active: false };
      const response = await fetch(`${API_BASE}/admin/registrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode },
        body: JSON.stringify({
          student: { firstName: creatingForStudent.firstName, lastName: creatingForStudent.lastName, class: creatingForStudent.class },
          parentContact: { ...createData.parentContact, phone: fullPhone },
          participants: createData.participants,
          delegation
        })
      });
      if (response.ok) {
        setPanelSuccess('Registrazione creata.');
        fetchData();
        setTimeout(() => closePanel(), 1500);
      } else {
        const data = await response.json();
        setPanelError(data.error || 'Errore nel/nella creazione.');
      }
    } catch (err) { setPanelError('Errore nel/nella creazione della registrazione.'); }
  };

  // Delete registration
  const deleteRegistration = async (reg) => {
    if (!window.confirm(`Eliminare la registrazione di ${reg.student.firstName} ${reg.student.lastName}? Il genitore sarà notificato.`)) return;
    try {
      const response = await fetch(`${API_BASE}/admin/registrations/${reg.id}`, {
        method: 'DELETE',
        headers: { 'x-access-code': accessCode }
      });
      if (response.ok) { fetchData(); }
      else { const data = await response.json(); alert(data.error || 'Errore nel/nella eliminazione.'); }
    } catch (err) { alert('Errore nel/nella eliminazione della registrazione.'); }
  };

  // Create panel participant helpers
  const addCreateParticipant = () => {
    const updated = { ...createData };
    updated.participants.push({ firstName: '', lastName: '', relation: config.relations[1] || 'Genitore', menu: config.menus[0]?.name || 'Hamburger', siblingStudent: false, siblingClass: '' });
    setCreateData(updated);
  };

  const updateCreateParticipant = (index, field, value) => {
    const updated = { ...createData };
    updated.participants[index][field] = value;
    if (field === 'relation' && value !== 'Fratello/Sorella') {
      updated.participants[index].siblingStudent = false;
      updated.participants[index].siblingClass = '';
    }
    setCreateData(updated);
  };

  const removeCreateParticipant = (index) => {
    if (index === 0) return;
    const updated = { ...createData };
    updated.participants = updated.participants.filter((_, i) => i !== index);
    setCreateData(updated);
  };

  const toggleCreateDelegation = (active) => {
    const updated = { ...createData, delegationActive: active };
    if (active) { updated.participants = [updated.participants[0]]; }
    setCreateData(updated);
  };

  if (loading) return <p>Caricamento...</p>;

  const isDelegatedParentRegistered = (reg) => {
    if (!reg.delegation || !reg.delegation.active) return true;
    const ds = reg.delegation.delegatedStudent;
    return registrations.some(
      r => r.student.firstName.trim().replace(/\s+/g, ' ').toLowerCase() === ds.firstName.trim().replace(/\s+/g, ' ').toLowerCase() &&
           r.student.lastName.trim().replace(/\s+/g, ' ').toLowerCase() === ds.lastName.trim().replace(/\s+/g, ' ').toLowerCase() &&
           r.student.class === ds.class
    );
  };

  // Check if the delegated parent name matches a parent participant in the delegated student's registration
  const isDelegatedParentMatchingRegistration = (reg) => {
    if (!reg.delegation || !reg.delegation.active) return { match: true };
    const ds = reg.delegation.delegatedStudent;
    const dp = reg.delegation.delegatedParent;
    
    // Find the delegated student's registration
    const delegatedReg = registrations.find(
      r => r.student.firstName.trim().replace(/\s+/g, ' ').toLowerCase() === ds.firstName.trim().replace(/\s+/g, ' ').toLowerCase() &&
           r.student.lastName.trim().replace(/\s+/g, ' ').toLowerCase() === ds.lastName.trim().replace(/\s+/g, ' ').toLowerCase() &&
           r.student.class === ds.class
    );
    
    if (!delegatedReg) return { match: false, reason: 'no-registration' };
    
    // Check if any parent participant matches the delegated parent name
    const parentMatch = delegatedReg.participants.some(
      p => (p.relation === 'Genitore' || p.relation === 'Altro Genitore') &&
           p.firstName.trim().toLowerCase() === dp.firstName.trim().toLowerCase() &&
           p.lastName.trim().toLowerCase() === dp.lastName.trim().toLowerCase()
    );
    
    if (!parentMatch) return { match: false, reason: 'name-mismatch', delegatedReg };
    
    return { match: true };
  };

  let grandTotalReg = 0, grandTotalStudents = 0, grandTotalCost = 0, grandTotalPaid = 0, grandTotalMissing = 0, grandTotalCheckedIn = 0;
  const classDataMap = {};
  for (const cls of classes) {
    const data = getClassData(cls);
    classDataMap[cls] = data;
    grandTotalReg += data.totalRegistered;
    grandTotalStudents += data.studentRows.length;
    grandTotalCost += data.totalCost;
    grandTotalPaid += data.totalPaid;
    grandTotalMissing += data.totalMissing;
    grandTotalCheckedIn += data.totalCheckedIn;
  }
  const activePanel = viewingReg || editingReg;

  return (
    <div>
      <h2>Dashboard</h2>

      <div className="sticky-grand-total">
        <div className="sticky-total-items">
          <div className="sticky-total-item">
            <span className="sticky-total-label">Registrati</span>
            <span className="sticky-total-value">{grandTotalReg}/{grandTotalStudents}</span>
          </div>
          <div className="sticky-total-item">
            <span className="sticky-total-label">Totale</span>
            <span className="sticky-total-value">€{grandTotalCost}</span>
          </div>
          <div className="sticky-total-item sticky-total-paid">
            <span className="sticky-total-label">Pagato</span>
            <span className="sticky-total-value">€{grandTotalPaid}</span>
          </div>
          <div className="sticky-total-item sticky-total-missing">
            <span className="sticky-total-label">Mancante</span>
            <span className="sticky-total-value">€{grandTotalMissing}</span>
          </div>
          <div className="sticky-total-item sticky-total-checkin">
            <span className="sticky-total-label">Entrati</span>
            <span className="sticky-total-value">{grandTotalCheckedIn}/{grandTotalReg}</span>
          </div>
        </div>
      </div>

      {isOrganizer && (
        <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button className="btn-secondary" onClick={collapseAll}>Comprimi Tutto</button>
          <button className="btn-secondary" onClick={expandAll}>Espandi Tutto</button>
          <span style={{ flex: 1 }}></span>
          <button className="btn-download-sm" onClick={() => window.open(`${API_BASE}/export/dashboard/excel?code=${accessCode}`, '_blank')}>📊 Excel Tutte</button>
          <button className="btn-download-sm" onClick={() => window.open(`${API_BASE}/export/dashboard/pdf?code=${accessCode}`, '_blank')}>📄 PDF Tutte</button>
        </div>
      )}

      {classes.map(cls => {
        const data = classDataMap[cls];
        const isCollapsed = collapsedClasses[cls];

        return (
          <div key={cls} className="dashboard-class">
            <div className="dashboard-class-header" onClick={() => isOrganizer && toggleCollapse(cls)} style={{ cursor: isOrganizer ? 'pointer' : 'default' }}>
              <h3>
                {isOrganizer && <span className="collapse-icon">{isCollapsed ? '▶' : '▼'}</span>}
                Classe {cls}
              </h3>
              <div className="class-header-actions" onClick={(e) => e.stopPropagation()}>
                <button className="btn-export-sm" title="Excel classe" onClick={() => window.open(`${API_BASE}/export/dashboard/excel?class=${cls}&code=${accessCode}`, '_blank')}>📊</button>
                <button className="btn-export-sm" title="PDF classe" onClick={() => window.open(`${API_BASE}/export/dashboard/pdf?class=${cls}&code=${accessCode}`, '_blank')}>📄</button>
              </div>
              {isCollapsed && (
                <div className="dashboard-summary-inline">
                  <span>{data.totalRegistered}/{data.studentRows.length} registrati</span>
                  <span>Totale: €{data.totalCost}</span>
                  <span>Pagato: €{data.totalPaid}</span>
                  <span>Mancante: €{data.totalMissing}</span>
                  <span>Entrati: {data.totalCheckedIn}/{data.totalRegistered}</span>
                </div>
              )}
            </div>
            {!isCollapsed && (
              <>
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th className="sortable-th" onClick={() => toggleSort('student')}>
                        <span>Studente{sortIcon('student')}</span>
                        <input
                          type="text"
                          className="th-filter-input"
                          placeholder="Cerca..."
                          value={filterStudent}
                          onChange={(e) => setFilterStudent(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                      <th className="sortable-th">
                        <span onClick={() => toggleSort('registered')}>Registrato{sortIcon('registered')}</span>
                        <select className="th-filter" value={filterRegistered} onChange={(e) => setFilterRegistered(e.target.value)}>
                          <option value="all">Tutti</option>
                          <option value="yes">✓ Sì</option>
                          <option value="no">✗ No</option>
                        </select>
                      </th>
                      <th>Partecipanti</th>
                      <th>Totale</th>
                      <th className="sortable-th">
                        <span onClick={() => toggleSort('paid')}>Pagato{sortIcon('paid')}</span>
                        <select className="th-filter" value={filterPaid} onChange={(e) => setFilterPaid(e.target.value)}>
                          <option value="all">Tutti</option>
                          <option value="yes">✓ Pagato</option>
                          <option value="no">✗ Non pagato</option>
                        </select>
                      </th>
                      <th className="sortable-th">
                        <span>Liberatoria</span>
                        <select className="th-filter" value={filterLiberatoria} onChange={(e) => setFilterLiberatoria(e.target.value)}>
                          <option value="all">Tutti</option>
                          <option value="yes">✓ Ricevuta</option>
                          <option value="no">✗ Mancante</option>
                        </select>
                      </th>
                      <th>Check-in</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applyFiltersAndSort(data.studentRows).map(({ student: s, registration: reg, siblingInfo }) => (
                      <tr key={s.id} className={reg ? '' : (siblingInfo ? 'sibling-included' : 'not-registered')}>
                        <td data-label="">
                          {editingStudentId === s.id ? (
                            <div className="inline-edit-name">
                              <input type="text" value={editStudentFirst} onChange={(e) => setEditStudentFirst(e.target.value)}
                                placeholder="Nome" style={{ width: '80px', fontSize: '12px', padding: '3px 5px' }} />
                              <input type="text" value={editStudentLast} onChange={(e) => setEditStudentLast(e.target.value)}
                                placeholder="Cognome" style={{ width: '80px', fontSize: '12px', padding: '3px 5px' }} />
                              <button className="btn-primary" onClick={() => saveStudentName(s.id)} style={{ fontSize: '11px', padding: '3px 8px' }}>✓</button>
                              <button className="btn-secondary" onClick={() => setEditingStudentId(null)} style={{ fontSize: '11px', padding: '3px 8px' }}>✗</button>
                            </div>
                          ) : (
                            <span className="editable-name" onClick={() => { setEditingStudentId(s.id); setEditStudentFirst(s.firstName); setEditStudentLast(s.lastName); }}
                              title="Clicca per modificare il nome">
                              {s.firstName} {s.lastName}
                            </span>
                          )}
                        </td>
                        <td data-label="Registrato: ">
                          {reg ? '✓' : (siblingInfo ? '✓' : '✗')}
                          {reg && reg.delegation && reg.delegation.active ? (
                            reg.delegation.mode === 'electronic' ? (
                              reg.delegation.delegatingParentSigned && reg.delegation.delegatedParentSigned ? ' 🤝2/2' :
                              reg.delegation.delegatingParentSigned ? ' 🤝1/2' : ' 🤝'
                            ) : ' 🤝'
                          ) : ''}
                          {reg && reg.delegation && reg.delegation.active && !isDelegatedParentRegistered(reg) ? ' ⚠️' : ''}
                          {reg && reg.delegation && reg.delegation.active && isDelegatedParentRegistered(reg) && !isDelegatedParentMatchingRegistration(reg).match ? ' ⚠️' : ''}
                          {!reg && siblingInfo ? ' 👨‍👩‍👧' : ''}
                        </td>
                        <td data-label="Partecipanti: ">
                          {reg ? reg.participants.length : (siblingInfo
                            ? <span className="sibling-tag">Con {siblingInfo.registration.student.firstName} {siblingInfo.registration.student.lastName}</span>
                            : '-')}
                        </td>
                        <td data-label="Totale: ">{reg ? `€${getRegistrationCost(reg)}` : (siblingInfo ? <span className="sibling-tag">Incluso</span> : '-')}</td>
                        <td data-label="Pagato: ">
                          {reg ? (
                            <button className={`btn-payment ${reg.paid ? 'paid' : 'unpaid'}`} onClick={() => !isRepLocked && togglePayment(reg.id, reg.paid)} disabled={isRepLocked}>
                              {reg.paid ? '✓ Pagato' : '✗ Non pagato'}
                            </button>
                          ) : '-'}
                        </td>
                        <td data-label="Liberatoria: ">
                          {reg ? (
                            reg.liberatoria && reg.liberatoria.signedElectronically ? (
                              <span className="btn-payment paid" style={{ cursor: 'default' }}>✓ Firmata</span>
                            ) : (
                              <button className={`btn-payment ${reg.liberatoriaReceived ? 'paid' : 'unpaid'}`} onClick={() => !isRepLocked && toggleLiberatoria(reg.id, reg.liberatoriaReceived)} disabled={isRepLocked}>
                                {reg.liberatoriaReceived ? '✓ Ricevuta' : '✗ Mancante'}
                              </button>
                            )
                          ) : '-'}
                        </td>
                        <td data-label="Check-in: ">
                          {reg ? (
                            <button
                              className={`btn-payment ${reg.checkedIn ? 'paid' : (reg.checkedInPartial ? 'partial' : 'unpaid')}`}
                              onClick={() => { if (!isRepLocked) window.open(`/verify/${reg.editToken}`, '_blank'); }}
                              disabled={isRepLocked}
                              style={reg.checkedInPartial ? { background: '#2980b9', color: '#fff' } : {}}
                            >
                              {reg.checkedIn ? '✓ Entrato' : (reg.checkedInPartial ? '~ Parziale' : '✗ Non entrato')}
                            </button>
                          ) : '-'}
                        </td>
                        <td>
                          {reg ? (
                            <div className="action-icons">
                              <button className="icon-btn" title="Visualizza dettagli" onClick={() => openView(reg)}>👁</button>
                              {!isRepLocked && <button className="icon-btn" title="Modifica registrazione" onClick={() => openEdit(reg)}>✏️</button>}
                              <div className="pdf-dropdown-wrapper">
                                <button className="icon-btn" title="Scarica PDF" onClick={(e) => { e.stopPropagation(); togglePdfDropdown(reg.id); }}>📄</button>
                                {pdfDropdownId === reg.id && (
                                  <div className="pdf-dropdown">
                                    <button onClick={() => { window.open(`${API_BASE}/registrations/${reg.id}/pdf/registration?code=${accessCode}`, '_blank'); setPdfDropdownId(null); }}>
                                      📋 PDF Registrazione
                                    </button>
                                    {reg.delegation && reg.delegation.active && (
                                      <button onClick={() => { window.open(`${API_BASE}/registrations/${reg.id}/pdf/delegation?code=${accessCode}`, '_blank'); setPdfDropdownId(null); }}>
                                        🤝 PDF Delega
                                      </button>
                                    )}
                                    <button onClick={() => { window.open(`${API_BASE}/registrations/${reg.id}/pdf/liberatoria?code=${accessCode}`, '_blank'); setPdfDropdownId(null); }}>
                                      📝 PDF Liberatoria
                                    </button>
                                  </div>
                                )}
                              </div>
                              {!isRepLocked && <button className="icon-btn" title="Elimina registrazione" onClick={() => deleteRegistration(reg)}>🗑</button>}
                            </div>
                          ) : (siblingInfo ? (
                            <div className="action-icons">
                              <button className="icon-btn" title="Visualizza registrazione del fratello/sorella" onClick={() => openView(siblingInfo.registration)}>👁</button>
                            </div>
                          ) : (
                            <div className="action-icons">
                              {!isRepLocked && <button className="icon-btn" title="Crea registrazione" onClick={() => openCreate(s)}>➕</button>}
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="dashboard-summary">
                  <span>Registrato: <strong>{data.totalRegistered}</strong> / {data.studentRows.length}</span>
                  <span>Totale: <strong>€{data.totalCost}</strong></span>
                  <span className="paid-amount">Pagato: <strong>€{data.totalPaid}</strong></span>
                  <span className="missing-amount">Mancante: <strong>€{data.totalMissing}</strong></span>
                  <span style={{ color: '#1a73e8' }}>Entrati: <strong>{data.totalCheckedIn}/{data.totalRegistered}</strong></span>
                </div>
              </>
            )}
          </div>
        );
      })}

      {/* View Panel */}
      {viewingReg && (
        <div className="panel-overlay" onClick={closePanel}>
          <div className="panel" onClick={(e) => e.stopPropagation()}>
            <div className="panel-header">
              <h3>Dettagli Registrazione</h3>
              <button className="icon-btn" onClick={closePanel}>✕</button>
            </div>
            <div className="panel-body">
              <div className="detail-row"><strong>Studente:</strong> {viewingReg.student.firstName} {viewingReg.student.lastName}</div>
              <div className="detail-row"><strong>Classe:</strong> {viewingReg.student.class}</div>
              <div className="detail-row"><strong>Email:</strong> {viewingReg.parentContact.email}</div>
              <div className="detail-row"><strong>Telefono:</strong> {viewingReg.parentContact.phone}</div>
              <div className="detail-row"><strong>Registrato:</strong> {new Date(viewingReg.timestamp).toLocaleString()}</div>
              <div className="detail-row"><strong>Pagamento:</strong> {viewingReg.paid ? '✓ Pagato' : '✗ Non pagato'}</div>

              {/* Liberatoria signature info */}
              <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f0fff4', borderRadius: '4px', border: '1px solid #b7e4c7' }}>
                <h4 style={{ marginBottom: '8px' }}>📝 Liberatoria</h4>
                {viewingReg.liberatoria && viewingReg.liberatoria.mode === 'electronic' ? (
                  viewingReg.liberatoria.signedElectronically ? (
                    <div>
                      <div className="detail-row"><strong>Modalità:</strong> ✍️ Firma elettronica</div>
                      <div className="detail-row"><strong>Stato:</strong> <span style={{ color: '#27ae60' }}>✓ Firmata</span></div>
                      <div className="detail-row"><strong>Data:</strong> {viewingReg.liberatoria.signedAt ? new Date(viewingReg.liberatoria.signedAt).toLocaleString('it-IT') : 'N/A'}</div>
                      <div className="detail-row"><strong>Email:</strong> {viewingReg.liberatoria.signedByEmail || 'N/A'}</div>
                      <div className="detail-row"><strong>Rif. OTP:</strong> {viewingReg.liberatoria.otpRef || 'N/A'}</div>
                    </div>
                  ) : (
                    <div>
                      <div className="detail-row"><strong>Modalità:</strong> ✍️ Firma elettronica</div>
                      <div className="detail-row"><strong>Stato:</strong> <span style={{ color: '#e67e22' }}>⏳ In attesa di firma</span></div>
                    </div>
                  )
                ) : (
                  <div>
                    <div className="detail-row"><strong>Modalità:</strong> 📄 Firma cartacea</div>
                    <div className="detail-row"><strong>Stato:</strong> {viewingReg.liberatoriaReceived ? <span style={{ color: '#27ae60' }}>✓ Ricevuta</span> : <span style={{ color: '#e74c3c' }}>✗ Mancante</span>}</div>
                  </div>
                )}
              </div>

              {viewingReg.delegation && viewingReg.delegation.active && (
                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f0f7ff', borderRadius: '4px', border: '1px solid #b3d4fc' }}>
                  <h4 style={{ marginBottom: '8px' }}>Delega</h4>
                  <div className="detail-row">
                    <strong>Delegato al genitore:</strong> {viewingReg.delegation.delegatedParent.firstName} {viewingReg.delegation.delegatedParent.lastName}
                  </div>
                  <div className="detail-row">
                    <strong>Genitore dello studente:</strong> {viewingReg.delegation.delegatedStudent.firstName} {viewingReg.delegation.delegatedStudent.lastName} (Classe {viewingReg.delegation.delegatedStudent.class})
                  </div>
                  {/* E-sign status for delegation */}
                  {viewingReg.delegation.mode && (
                    <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#e8f4fd', borderRadius: '4px' }}>
                      <div className="detail-row">
                        <strong>Modalità firma:</strong>{' '}
                        {viewingReg.delegation.mode === 'paper' ? '📄 Cartacea' : '✍️ Elettronica'}
                      </div>
                      {viewingReg.delegation.mode === 'electronic' && (
                        <div>
                          <div className="detail-row" style={{ marginTop: '4px' }}>
                            <strong>Genitore delegante:</strong>{' '}
                            {viewingReg.delegation.delegatingParentSigned
                              ? <span style={{ color: '#27ae60' }}>✓ Firmato{viewingReg.delegation.delegatingParentSignedAt ? ` il ${new Date(viewingReg.delegation.delegatingParentSignedAt).toLocaleString('it-IT')}` : ''}</span>
                              : <span style={{ color: '#e67e22' }}>⏳ In attesa</span>
                            }
                          </div>
                          <div className="detail-row" style={{ marginTop: '4px' }}>
                            <strong>Genitore delegato:</strong>{' '}
                            {viewingReg.delegation.delegatedParentSigned
                              ? <span style={{ color: '#27ae60' }}>✓ Firmato{viewingReg.delegation.delegatedParentSignedAt ? ` il ${new Date(viewingReg.delegation.delegatedParentSignedAt).toLocaleString('it-IT')}` : ''}</span>
                              : <span style={{ color: '#e67e22' }}>⏳ In attesa controfirma</span>
                            }
                          </div>
                          {viewingReg.delegation.delegatedParent.email && (
                            <div className="detail-row" style={{ marginTop: '4px' }}>
                              <strong>Email delegato:</strong> {viewingReg.delegation.delegatedParent.email}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {/* No mode set — old registration, show nothing extra */}
                  {!viewingReg.delegation.mode && (
                    <div className="detail-row" style={{ marginTop: '8px' }}>
                      <strong>Firma delega:</strong> 📄 Cartacea (pre-firma elettronica)
                    </div>
                  )}
                  {/* Reminder button for pending countersign */}
                  {viewingReg.delegation.mode === 'electronic' &&
                   viewingReg.delegation.delegatingParentSigned &&
                   !viewingReg.delegation.delegatedParentSigned && (
                    <button
                      className="btn-secondary"
                      style={{ marginTop: '10px', fontSize: '13px' }}
                      onClick={async () => {
                        try {
                          const response = await fetch(`${API_BASE}/admin/registrations/${viewingReg.id}/send-delegation-reminder`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode }
                          });
                          if (response.ok) { alert('Promemoria inviato con successo.'); }
                          else { const d = await response.json(); alert(d.error || 'Errore nell\'invio del promemoria.'); }
                        } catch (err) { alert('Errore di connessione.'); }
                      }}
                    >
                      📧 Invia promemoria al genitore delegato
                    </button>
                  )}
                  {!isDelegatedParentRegistered(viewingReg) && (
                    <div className="warning-box" style={{ marginTop: '10px' }}>
                      ⚠️ Lo studente delegato ({viewingReg.delegation.delegatedStudent.firstName} {viewingReg.delegation.delegatedStudent.lastName}) non ha una registrazione. Il genitore delegato non potrà accedere alla festa.
                    </div>
                  )}
                  {isDelegatedParentRegistered(viewingReg) && (() => {
                    const check = isDelegatedParentMatchingRegistration(viewingReg);
                    if (!check.match && check.reason === 'name-mismatch') {
                      const dp = viewingReg.delegation.delegatedParent;
                      const ds = viewingReg.delegation.delegatedStudent;
                      const parents = check.delegatedReg.participants
                        .filter(p => p.relation === 'Genitore' || p.relation === 'Altro Genitore')
                        .map(p => `${p.firstName} ${p.lastName}`);
                      return (
                        <div className="warning-box" style={{ marginTop: '10px' }}>
                          ⚠️ <strong>Nome non corrispondente:</strong> Il genitore delegato <strong>{dp.firstName} {dp.lastName}</strong> non corrisponde a nessun genitore nella registrazione di {ds.firstName} {ds.lastName}.
                          {parents.length > 0
                            ? <> Il/I genitore/i registrato/i di {ds.firstName} sono: <strong>{parents.join(', ')}</strong>. Verificare i dettagli della delega.</>
                            : <> Nessun genitore è presente nella registrazione di {ds.firstName}.</>
                          }
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}

              <h4 style={{ marginTop: '15px' }}>Partecipanti ({viewingReg.participants.length})</h4>
              {viewingReg.participants.map((p, i) => (
                <div key={i} className="participant-detail">
                  {p.firstName} {p.lastName} — {p.relation} — {p.menu} (€{getMenuPrice(p.menu)})
                  {p.siblingStudent && p.siblingClass && (
                    <span className="sibling-tag" style={{ marginLeft: '8px' }}>
                      Studente nella classe {p.siblingClass}
                      {registrations.some(r =>
                        r.student.firstName.toLowerCase() === p.firstName.trim().toLowerCase() &&
                        r.student.lastName.toLowerCase() === p.lastName.trim().toLowerCase() &&
                        r.student.class === p.siblingClass
                      ) ? ' ✓ Registrato' : ' ✗ Non registrato'}
                    </span>
                  )}
                </div>
              ))}
              <div style={{ marginTop: '10px', fontSize: '16px', fontWeight: 'bold' }}>
                Totale: €{getRegistrationCost(viewingReg)}
              </div>
            </div>
            <div className="panel-footer">
              <button className="btn-primary" onClick={openEditFromView}>Modifica Registrazione</button>
              <button className="btn-download-sm" onClick={() => window.open(`${API_BASE}/registrations/${viewingReg.id}/pdf/registration?code=${accessCode}`, '_blank')}>
                📄 PDF Registrazione
              </button>
              {viewingReg.delegation && viewingReg.delegation.active && (
                <button className="btn-download-sm" onClick={() => window.open(`${API_BASE}/registrations/${viewingReg.id}/pdf/delegation?code=${accessCode}`, '_blank')}>
                  📄 PDF Delega
                </button>
              )}
              <button className="btn-download-sm" onClick={() => window.open(`${API_BASE}/registrations/${viewingReg.id}/pdf/liberatoria?code=${accessCode}`, '_blank')}>
                📄 PDF Liberatoria
              </button>
              <button className="btn-secondary" onClick={closePanel}>Chiudi</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Panel */}
      {editingReg && editData && (
        <div className="panel-overlay" onClick={closePanel}>
          <div className="panel panel-wide" onClick={(e) => e.stopPropagation()}>
            <div className="panel-header">
              <h3>Modifica: {editingReg.student.firstName} {editingReg.student.lastName} ({editingReg.student.class})</h3>
              <button className="icon-btn" onClick={closePanel}>✕</button>
            </div>
            <div className="panel-body">
              <div className="form-group">
                <label>Email del Genitore</label>
                <input type="email" value={editData.parentContact.email}
                  onChange={(e) => setEditData({ ...editData, parentContact: { ...editData.parentContact, email: e.target.value } })} />
              </div>
              <div className="form-group">
                <label>Telefono del Genitore</label>
                <div className="phone-input-group">
                  <select value={editData.parentContact.countryCode} className="country-code-select"
                    onChange={(e) => setEditData({ ...editData, parentContact: { ...editData.parentContact, countryCode: e.target.value } })}>
                    {countryCodes.map(item => <option key={item.code} value={item.code}>{item.code} ({item.country})</option>)}
                  </select>
                  <input type="tel" value={editData.parentContact.phone} className="phone-number-input"
                    onChange={(e) => setEditData({ ...editData, parentContact: { ...editData.parentContact, phone: e.target.value.replace(/\D/g, '') } })} />
                </div>
              </div>

              {/* Delegation Section */}
              <div className="delegation-section" style={{ margin: '15px 0' }}>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={editData.delegationActive}
                      onChange={(e) => toggleEditDelegation(e.target.checked)} />
                    Delega (bambino accompagnato dal genitore di un altro studente)
                  </label>
                </div>
                {editData.delegationActive && (
                  <div className="delegation-form">
                    <h4>Studente Delegato</h4>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Nome</label>
                        <input type="text" value={editData.delegation.delegatedStudent.firstName}
                          onChange={(e) => setEditData({ ...editData, delegation: { ...editData.delegation, delegatedStudent: { ...editData.delegation.delegatedStudent, firstName: e.target.value } } })} />
                      </div>
                      <div className="form-group">
                        <label>Cognome</label>
                        <input type="text" value={editData.delegation.delegatedStudent.lastName}
                          onChange={(e) => setEditData({ ...editData, delegation: { ...editData.delegation, delegatedStudent: { ...editData.delegation.delegatedStudent, lastName: e.target.value } } })} />
                      </div>
                      <div className="form-group">
                        <label>Classe</label>
                        <select value={editData.delegation.delegatedStudent.class}
                          onChange={(e) => setEditData({ ...editData, delegation: { ...editData.delegation, delegatedStudent: { ...editData.delegation.delegatedStudent, class: e.target.value } } })}>
                          <option value="">Seleziona classe</option>
                          {(config.classes || ['1A','1B','1C','2A','2B','2C','3A','3B','3C']).map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                    <h4>Genitore Delegato</h4>
                    <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                      <div className="form-group">
                        <label>Nome</label>
                        <input type="text" value={editData.delegation.delegatedParent.firstName}
                          onChange={(e) => setEditData({ ...editData, delegation: { ...editData.delegation, delegatedParent: { ...editData.delegation.delegatedParent, firstName: e.target.value } } })} />
                      </div>
                      <div className="form-group">
                        <label>Cognome</label>
                        <input type="text" value={editData.delegation.delegatedParent.lastName}
                          onChange={(e) => setEditData({ ...editData, delegation: { ...editData.delegation, delegatedParent: { ...editData.delegation.delegatedParent, lastName: e.target.value } } })} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <h4>Partecipanti</h4>
              {editData.participants.map((participant, index) => (
                <div key={index} className="participant-item">
                  <div className="participant-header">
                    <strong>Partecipante {index + 1}</strong>
                    {index > 0 && <button className="btn-danger" onClick={() => removeParticipant(index)}>Rimuovi</button>}
                  </div>
                  <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
                    <div className="form-group">
                      <label>Nome</label>
                      <input type="text" value={participant.firstName} disabled={index === 0}
                        onChange={(e) => updateParticipant(index, 'firstName', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Cognome</label>
                      <input type="text" value={participant.lastName} disabled={index === 0}
                        onChange={(e) => updateParticipant(index, 'lastName', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Relazione</label>
                      <select value={participant.relation} disabled={index === 0}
                        onChange={(e) => updateParticipant(index, 'relation', e.target.value)}>
                        {config.relations.map(rel => {
                          const genitoreExists = editData.participants.some((p, i) => i !== index && p.relation === 'Genitore');
                          const disabled = rel === 'Genitore' && genitoreExists && participant.relation !== 'Genitore';
                          return <option key={rel} value={rel} disabled={disabled}>{rel}{disabled ? ' (già presente)' : ''}</option>;
                        })}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Menù</label>
                      <select value={participant.menu}
                        onChange={(e) => updateParticipant(index, 'menu', e.target.value)}>
                        {config.menus.map(menu => <option key={menu.name} value={menu.name}>{menu.name} (€{menu.price})</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              ))}

              {!editData.delegationActive && (
                <button className="btn-secondary" onClick={addParticipant} style={{ marginTop: '10px' }}>Aggiungi Partecipante</button>
              )}

              <div style={{ marginTop: '15px', fontSize: '16px', fontWeight: 'bold' }}>
                Totale: €{editData.participants.reduce((sum, p) => sum + getMenuPrice(p.menu), 0)}
              </div>

              {panelError && <div className="error" style={{ marginTop: '10px' }}>{panelError}</div>}
              {panelSuccess && <div className="success" style={{ marginTop: '10px' }}>{panelSuccess}</div>}
            </div>
            <div className="panel-footer">
              <button className="btn-primary" onClick={saveEdit}>Salva Modifiche</button>
              <button className="btn-secondary" onClick={closePanel}>Annulla</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Panel */}
      {creatingForStudent && createData && (
        <div className="panel-overlay" onClick={closePanel}>
          <div className="panel panel-wide" onClick={(e) => e.stopPropagation()}>
            <div className="panel-header">
              <h3>Nuova Registrazione: {creatingForStudent.firstName} {creatingForStudent.lastName} ({creatingForStudent.class})</h3>
              <button className="icon-btn" onClick={closePanel}>✕</button>
            </div>
            <div className="panel-body">
              <div className="form-group">
                <label>Email del Genitore *</label>
                <input type="email" value={createData.parentContact.email}
                  onChange={(e) => setCreateData({ ...createData, parentContact: { ...createData.parentContact, email: e.target.value } })} />
              </div>
              <div className="form-group">
                <label>Telefono del Genitore *</label>
                <div className="phone-input-group">
                  <select value={createData.parentContact.countryCode} className="country-code-select"
                    onChange={(e) => setCreateData({ ...createData, parentContact: { ...createData.parentContact, countryCode: e.target.value } })}>
                    {countryCodes.map(item => <option key={item.code} value={item.code}>{item.code} ({item.country})</option>)}
                  </select>
                  <input type="tel" value={createData.parentContact.phone} className="phone-number-input"
                    onChange={(e) => setCreateData({ ...createData, parentContact: { ...createData.parentContact, phone: e.target.value.replace(/\D/g, '') } })} />
                </div>
              </div>

              {/* Delegation */}
              <div className="delegation-section" style={{ margin: '15px 0' }}>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={createData.delegationActive}
                      onChange={(e) => toggleCreateDelegation(e.target.checked)} />
                    Delega (bambino accompagnato dal genitore di un altro studente)
                  </label>
                </div>
                {createData.delegationActive && (
                  <div className="delegation-form">
                    <h4>Studente Delegato</h4>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Nome</label>
                        <input type="text" value={createData.delegation.delegatedStudent.firstName}
                          onChange={(e) => setCreateData({ ...createData, delegation: { ...createData.delegation, delegatedStudent: { ...createData.delegation.delegatedStudent, firstName: e.target.value } } })} />
                      </div>
                      <div className="form-group">
                        <label>Cognome</label>
                        <input type="text" value={createData.delegation.delegatedStudent.lastName}
                          onChange={(e) => setCreateData({ ...createData, delegation: { ...createData.delegation, delegatedStudent: { ...createData.delegation.delegatedStudent, lastName: e.target.value } } })} />
                      </div>
                      <div className="form-group">
                        <label>Classe</label>
                        <select value={createData.delegation.delegatedStudent.class}
                          onChange={(e) => setCreateData({ ...createData, delegation: { ...createData.delegation, delegatedStudent: { ...createData.delegation.delegatedStudent, class: e.target.value } } })}>
                          <option value="">Seleziona classe</option>
                          {(config.classes || ['1A','1B','1C','2A','2B','2C','3A','3B','3C']).map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                    <h4>Genitore Delegato</h4>
                    <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                      <div className="form-group">
                        <label>Nome</label>
                        <input type="text" value={createData.delegation.delegatedParent.firstName}
                          onChange={(e) => setCreateData({ ...createData, delegation: { ...createData.delegation, delegatedParent: { ...createData.delegation.delegatedParent, firstName: e.target.value } } })} />
                      </div>
                      <div className="form-group">
                        <label>Cognome</label>
                        <input type="text" value={createData.delegation.delegatedParent.lastName}
                          onChange={(e) => setCreateData({ ...createData, delegation: { ...createData.delegation, delegatedParent: { ...createData.delegation.delegatedParent, lastName: e.target.value } } })} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <h4>Partecipanti</h4>
              {createData.participants.map((participant, index) => (
                <div key={index} className="participant-item">
                  <div className="participant-header">
                    <strong>Partecipante {index + 1}</strong>
                    {index > 0 && <button className="btn-danger" onClick={() => removeCreateParticipant(index)}>Rimuovi</button>}
                  </div>
                  <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
                    <div className="form-group">
                      <label>Nome</label>
                      <input type="text" value={participant.firstName} disabled={index === 0}
                        onChange={(e) => updateCreateParticipant(index, 'firstName', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Cognome</label>
                      <input type="text" value={participant.lastName} disabled={index === 0}
                        onChange={(e) => updateCreateParticipant(index, 'lastName', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Relazione</label>
                      <select value={participant.relation} disabled={index === 0}
                        onChange={(e) => updateCreateParticipant(index, 'relation', e.target.value)}>
                        {config.relations.map(rel => {
                          const genitoreExists = createData.participants.some((p, i) => i !== index && p.relation === 'Genitore');
                          const disabled = rel === 'Genitore' && genitoreExists && participant.relation !== 'Genitore';
                          return <option key={rel} value={rel} disabled={disabled}>{rel}{disabled ? ' (già presente)' : ''}</option>;
                        })}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Menù</label>
                      <select value={participant.menu}
                        onChange={(e) => updateCreateParticipant(index, 'menu', e.target.value)}>
                        {config.menus.map(menu => <option key={menu.name} value={menu.name}>{menu.name} (€{menu.price})</option>)}
                      </select>
                    </div>
                  </div>
                  {participant.relation === 'Fratello/Sorella' && index > 0 && (
                    <div className="sibling-student-section">
                      <div className="checkbox-group">
                        <label className="checkbox-label">
                          <input type="checkbox" checked={participant.siblingStudent || false}
                            onChange={(e) => updateCreateParticipant(index, 'siblingStudent', e.target.checked)} />
                          Questo fratello/sorella è uno studente della scuola
                        </label>
                      </div>
                      {participant.siblingStudent && (
                        <div className="form-group" style={{ marginTop: '10px', maxWidth: '200px' }}>
                          <label>Classe del fratello/sorella</label>
                          <select value={participant.siblingClass || ''}
                            onChange={(e) => updateCreateParticipant(index, 'siblingClass', e.target.value)}>
                            <option value="">Seleziona classe</option>
                            {(config.classes || ['1A','1B','1C','2A','2B','2C','3A','3B','3C']).map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                      )}
                      <p style={{ fontSize: '12px', color: '#888', marginTop: '5px' }}>Solo per fratelli/sorelle.</p>
                    </div>
                  )}
                </div>
              ))}

              {!createData.delegationActive && (
                <button className="btn-secondary" onClick={addCreateParticipant} style={{ marginTop: '10px' }}>Aggiungi Partecipante</button>
              )}

              <div style={{ marginTop: '15px', fontSize: '16px', fontWeight: 'bold' }}>
                Totale: €{createData.participants.reduce((sum, p) => sum + getMenuPrice(p.menu), 0)}
              </div>

              {panelError && <div className="error" style={{ marginTop: '10px' }}>{panelError}</div>}
              {panelSuccess && <div className="success" style={{ marginTop: '10px' }}>{panelSuccess}</div>}
            </div>
            <div className="panel-footer">
              <button className="btn-primary" onClick={saveCreate}>Crea Registrazione</button>
              <button className="btn-secondary" onClick={closePanel}>Annulla</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
